// src/pages/Login/Login.tsx
import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { PublicLanguageSwitcher } from '../../components/PublicLanguageSwitcher';

const { Title, Text } = Typography;

interface LoginForm {
  email: string;
  password: string;
}

// Palette de couleurs sobre et professionnelle
const COLORS = {
  blue: '#0A3780',
  lightBlue: '#1B4F96',
  gray: '#666666',
  lightGray: '#F5F5F5',
  white: '#FFFFFF',
  border: '#E0E0E0',
};

// Logo NAUVATEK
const NauvatekLogo: React.FC<{ size?: number }> = ({ size = 150 }) => (
  <img 
    src="/LOGO.png"
    alt="NAUVATEK Logo"
    style={{
      width: size,
      height: size,
      objectFit: 'contain',
    }}
    onError={(e) => {
      console.error('Logo NAUVATEK non trouvé');
      e.currentTarget.style.display = 'none';
    }}
  />
);

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { signIn } = useAuth();
  const { t } = useAppTranslation();

  // Détection mobile
  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Identifiants de test : console uniquement (localhost), jamais affichés dans l’UI
  React.useEffect(() => {
    if (window.location.hostname !== 'localhost') return;
    console.info(
      '%c🧪 Comptes de test (localhost)',
      'color:#1890ff;font-weight:bold;',
      '\n🔧 Super Admin\n  Email: superadmin@leniddoux.com\n  Mot de passe: SuperAdmin123!\n' +
        '👑 Admin Multi-Business\n  Email: admin@leniddoux.com\n  Mot de passe: Admin123!\n' +
        '🥖 Boulangerie le nid doux\n  Email: boulangerie@leniddoux.com\n  Mot de passe: Boulangerie123!\n' +
        '💅 Démo — Services (nid doux)\n  Email: institut@leniddoux.com\n  Mot de passe: Institut123!'
    );
  }, []);

  const onFinish = async (values: LoginForm) => {
    setLoading(true);
    try {
      await signIn(values.email, values.password);
      message.success(t('login.success'));
    } catch (error: any) {
      console.error('Erreur de connexion:', error);

      let errorMessage = t('login.errorGeneric');
      if (error.code === 'auth/user-not-found') {
        errorMessage = t('login.errorUserNotFound');
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = t('login.errorWrongPassword');
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = t('login.errorInvalidEmail');
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = t('login.errorTooManyRequests');
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        background: COLORS.lightGray,
        overflow: 'hidden',
      }}
    >
      {/* Panneau gauche avec logo et branding */}
      <div
        style={{
          flex: isMobile ? 'none' : 1,
          height: isMobile ? 'auto' : '100vh',
          background: COLORS.blue,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: isMobile ? '32px 20px 24px 20px' : '20px',
          overflow: 'auto',
        }}
      >
        <div style={{ textAlign: 'center', color: COLORS.white }}>
          <div style={{ marginBottom: isMobile ? '16px' : '32px' }}>
            <NauvatekLogo size={isMobile ? 80 : 300} />
          </div>
          
          {!isMobile && (
            <div style={{ marginTop: '48px', maxWidth: '300px' }}>
              <Text 
                style={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  display: 'block',
                }}
              >
                {t('login.tagline')}
              </Text>
            </div>
          )}
        </div>
      </div>

      {/* Panneau droit avec formulaire */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: isMobile ? 'flex-start' : 'center',
          padding: isMobile ? '20px' : '20px',
          background: isMobile ? COLORS.white : `
            linear-gradient(135deg, 
              ${COLORS.white} 0%, 
              #fafbfc 50%, 
              ${COLORS.lightGray} 100%
            )
          `,
          height: isMobile ? 'calc(100vh - 160px)' : '100vh',
          overflow: 'auto',
          paddingTop: isMobile ? '20px' : '20px',
          position: 'relative',
        }}
      >
        {/* Motifs géométriques décoratifs (desktop seulement) */}
        {!isMobile && (
          <>
            {/* Cercles décoratifs */}
            <div
              style={{
                position: 'absolute',
                top: '10%',
                right: '8%',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: `linear-gradient(45deg, ${COLORS.blue}20, ${COLORS.blue}35)`,
                animation: 'float 6s ease-in-out infinite',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '60%',
                right: '15%',
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                background: `linear-gradient(45deg, ${COLORS.blue}15, ${COLORS.blue}28)`,
                animation: 'float 8s ease-in-out infinite reverse',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '20%',
                left: '5%',
                width: '90px',
                height: '90px',
                borderRadius: '50%',
                background: `linear-gradient(45deg, ${COLORS.blue}18, ${COLORS.blue}30)`,
                animation: 'float 7s ease-in-out infinite',
              }}
            />
            
            {/* Cercles avec bordures */}
            <div
              style={{
                position: 'absolute',
                top: '30%',
                left: '3%',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                border: `2px solid ${COLORS.blue}25`,
                background: 'transparent',
                animation: 'pulse 4s ease-in-out infinite',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '10%',
                right: '20%',
                width: '45px',
                height: '45px',
                borderRadius: '50%',
                border: `1px solid ${COLORS.blue}20`,
                background: 'transparent',
                animation: 'pulse 5s ease-in-out infinite reverse',
              }}
            />
            
            {/* Lignes géométriques plus visibles */}
            <div
              style={{
                position: 'absolute',
                top: '25%',
                left: '10%',
                width: '150px',
                height: '3px',
                background: `linear-gradient(90deg, transparent, ${COLORS.blue}40, transparent)`,
                transform: 'rotate(45deg)',
                animation: 'slideIn 10s ease-in-out infinite',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '35%',
                right: '8%',
                width: '180px',
                height: '2px',
                background: `linear-gradient(90deg, transparent, ${COLORS.blue}35, transparent)`,
                transform: 'rotate(-30deg)',
                animation: 'slideIn 12s ease-in-out infinite reverse',
              }}
            />
            
            {/* Formes hexagonales plus grandes */}
            <div
              style={{
                position: 'absolute',
                top: '45%',
                left: '8%',
                width: '35px',
                height: '35px',
                background: `${COLORS.blue}25`,
                clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
                animation: 'rotate 15s linear infinite',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '15%',
                left: '25%',
                width: '28px',
                height: '28px',
                background: `${COLORS.blue}20`,
                clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
                animation: 'rotate 20s linear infinite reverse',
              }}
            />
            
            {/* Carrés tournants */}
            <div
              style={{
                position: 'absolute',
                top: '70%',
                left: '15%',
                width: '25px',
                height: '25px',
                background: `${COLORS.blue}22`,
                transform: 'rotate(45deg)',
                animation: 'rotate 18s linear infinite',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '5%',
                right: '30%',
                width: '20px',
                height: '20px',
                background: `${COLORS.blue}18`,
                transform: 'rotate(45deg)',
                animation: 'rotate 25s linear infinite reverse',
              }}
            />
          </>
        )}

        {/* CSS Keyframes pour les animations */}
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px); opacity: 0.7; }
              50% { transform: translateY(-15px); opacity: 1; }
            }
            @keyframes pulse {
              0%, 100% { transform: scale(1); opacity: 0.6; }
              50% { transform: scale(1.1); opacity: 0.9; }
            }
            @keyframes slideIn {
              0%, 100% { transform: rotate(45deg) translateX(0px); opacity: 0.5; }
              50% { transform: rotate(45deg) translateX(15px); opacity: 0.8; }
            }
            @keyframes rotate {
              0% { transform: rotate(0deg); opacity: 0.6; }
              50% { opacity: 0.9; }
              100% { transform: rotate(360deg); opacity: 0.6; }
            }
          `}
        </style>
        <Card
          style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : '400px',
            boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.06)',
            borderRadius: isMobile ? '0' : '12px',
            border: isMobile ? 'none' : `1px solid ${COLORS.border}`,
            padding: isMobile ? '20px 0' : '40px 32px',
            background: isMobile ? 'transparent' : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: isMobile ? 'none' : 'blur(10px)',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '24px' : '32px' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
              <PublicLanguageSwitcher size="small" />
            </div>
            <Title 
              level={2} 
              style={{ 
                color: COLORS.blue,
                margin: '0 0 8px 0',
                fontSize: isMobile ? '24px' : '28px',
                fontWeight: '600',
              }}
            >
              {t('login.title')}
            </Title>
            <Text 
              style={{ 
                color: COLORS.gray,
                fontSize: isMobile ? '13px' : '14px',
              }}
            >
              {t('login.subtitle')}
            </Text>
          </div>

          <Form
            data-testid="login-form"
            name="login"
            onFinish={onFinish} 
            layout="vertical" 
            size={isMobile ? 'middle' : 'large'}
          >
            <Form.Item
              name="email"
              label={<span style={{ color: COLORS.blue, fontWeight: '500' }}>{t('login.email')}</span>}
              rules={[
                { required: true, message: t('login.emailRequired') },
                { type: 'email', message: t('login.emailInvalid') },
              ]}
            >
              <Input
                data-testid="email-input"
                prefix={<UserOutlined style={{ color: COLORS.gray }} />}
                placeholder={t('login.emailPlaceholder')}
                autoComplete="email"
                style={{
                  borderRadius: '4px',
                  padding: isMobile ? '10px 16px' : '12px 16px',
                  fontSize: '14px',
                  border: `1px solid ${COLORS.border}`,
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span style={{ color: COLORS.blue, fontWeight: '500' }}>{t('login.password')}</span>}
              rules={[
                { required: true, message: t('login.passwordRequired') },
                { min: 6, message: t('login.passwordMin') },
              ]}
            >
              <Input.Password
                data-testid="password-input"
                prefix={<LockOutlined style={{ color: COLORS.gray }} />}
                placeholder={t('login.passwordPlaceholder')}
                autoComplete="current-password"
                style={{
                  borderRadius: '4px',
                  padding: isMobile ? '10px 16px' : '12px 16px',
                  fontSize: '14px',
                  border: `1px solid ${COLORS.border}`,
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: '0', marginTop: isMobile ? '24px' : '32px' }}>
              <Button
                data-testid="login-button"
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                style={{
                  height: isMobile ? '44px' : '48px',
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: '500',
                  backgroundColor: COLORS.blue,
                  borderColor: COLORS.blue,
                  borderRadius: '4px',
                }}
              >
                {t('login.submit')}
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: isMobile ? '24px' : '32px', paddingTop: isMobile ? '20px' : '24px', borderTop: `1px solid ${COLORS.border}` }}>
            <Text 
              style={{ 
                fontSize: '12px',
                color: COLORS.gray,
              }}
            >
              © 2025 NAUVATEK. Tous droits réservés.
            </Text>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Login;